import { normalizeLocale } from '@/lib/utils/locale-shared';

export interface DateRange {
  checkIn: string;
  checkOut: string;
}

/**
 * Suppliers send a day either as a plain date or as a full local timestamp — both shapes are in
 * play across tenants. Anchoring a date-only value at local midnight keeps the day the supplier
 * meant instead of shifting it by the reader's timezone; a timestamp is already unambiguous.
 *
 * Kept dependency-free rather than reaching for `parseDateTime`: this module ships inside the MCP
 * widget, and date-fns does not fit its size budget.
 */
function parseSupplierDate(dateStr: string): Date | null {
  const date = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
  return isNaN(date.getTime()) ? null : date;
}

/** Read off the parsed date, never off the string: a timestamp splits on its own hyphens. */
function dayAndMonth(date: Date): { day: string; month: string } {
  return {
    day: String(date.getDate()).padStart(2, '0'),
    month: String(date.getMonth() + 1).padStart(2, '0'),
  };
}

/** Day/month separator; callers mirroring a property's own flow pass theirs. */
export const DEFAULT_DATE_SEPARATOR = '/';

export function formatShortDate({
  dateStr,
  separator = DEFAULT_DATE_SEPARATOR,
}: {
  dateStr: string;
  separator?: string;
}): string {
  const date = parseSupplierDate(dateStr);
  if (!date) return dateStr;
  const { day, month } = dayAndMonth(date);
  const year = date.getFullYear();
  return year === new Date().getFullYear()
    ? `${day}${separator}${month}`
    : `${day}${separator}${month}${separator}${String(year).slice(2)}`;
}

export function formatDateRange({
  range,
  separator,
}: {
  range: DateRange;
  separator?: string;
}): string {
  return `${formatShortDate({ dateStr: range.checkIn, separator })} – ${formatShortDate({ dateStr: range.checkOut, separator })}`;
}

/**
 * The stay, for a rooms header. Read off the unfiltered list: the dates belong to the search, so
 * they have to survive a filter that leaves no rooms to read them from.
 */
export function roomsHeaderMeta({
  rooms,
  separator,
}: {
  rooms: DateRange[];
  separator?: string;
}): string[] {
  return rooms[0] ? [formatDateRange({ range: rooms[0], separator })] : [];
}

/** One day/month format for every tenant and language; only the weekday name is localized. */
export function formatLongDate({
  dateStr,
  locale,
  separator = DEFAULT_DATE_SEPARATOR,
}: {
  dateStr: string;
  locale: string;
  separator?: string;
}): string {
  const date = parseSupplierDate(dateStr);
  if (!date) return dateStr;
  const dayName = new Intl.DateTimeFormat(normalizeLocale(locale), { weekday: 'short' }).format(
    date,
  );
  const { day, month } = dayAndMonth(date);
  const year = date.getFullYear();
  return year === new Date().getFullYear()
    ? `${dayName} ${day}${separator}${month}`
    : `${dayName} ${day}${separator}${month}${separator}${year}`;
}

export function formatDateRangeLong({
  range,
  locale,
  separator,
}: {
  range: DateRange;
  locale: string;
  separator?: string;
}): string {
  return `${formatLongDate({ dateStr: range.checkIn, locale, separator })} – ${formatLongDate({ dateStr: range.checkOut, locale, separator })}`;
}

const DATE_RANGE_DELIMITER = '::';

export function buildDateSelectOptions({
  dateRanges,
  locale,
  separator,
}: {
  dateRanges: DateRange[];
  locale: string;
  separator?: string;
}) {
  return dateRanges.map(range => ({
    value: `${range.checkIn}${DATE_RANGE_DELIMITER}${range.checkOut}`,
    label: formatDateRangeLong({ range, locale, separator }),
    triggerLabel: formatDateRange({ range, separator }),
  }));
}

export function parseDateSelectValue(value: string): DateRange {
  const [checkIn = '', checkOut = ''] = value.split(DATE_RANGE_DELIMITER);
  return { checkIn, checkOut };
}

export function getActiveDateValue(dateRange: DateRange | null | undefined): string {
  return dateRange ? `${dateRange.checkIn}${DATE_RANGE_DELIMITER}${dateRange.checkOut}` : '';
}
